import { useEffect, useRef, useState } from "react";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Tag, Palette, Upload, Image as ImageIcon, X, Bell, CalendarClock, Pencil, Save, Building2, Send, Loader2, Play, SlidersHorizontal, Minimize2, Maximize2, Rows2, Rows3, Rows4, Plug } from "lucide-react";
import PushNotificationsToggle from "@/components/PushNotificationsToggle";
import LockedPeriodsCard from "@/components/settings/LockedPeriodsCard";
import FlagTemplatesCard from "@/components/settings/FlagTemplatesCard";
import MailPreferencesCard from "@/components/settings/MailPreferencesCard";
import ARChaseTemplatesCard from "@/components/settings/ARChaseTemplatesCard";
import SettingsTabs, { useVisibleSettingsSections } from "@/components/settings/SettingsTabs";
import QBOCard from "@/components/settings/QBOCard";
import TransactionRulesCard from "@/components/settings/TransactionRulesCard";
import TeamMemberSlotsCard from "@/components/settings/TeamMemberSlotsCard";
import BankAccountSlotsCard from "@/components/settings/BankAccountSlotsCard";
import ReclaimSlotsCard from "@/components/settings/ReclaimSlotsCard";
import ThemePickerCard from "@/components/settings/ThemePickerCard";
import WorkspaceSlugCard from "@/components/settings/WorkspaceSlugCard";
import { useRowDensity } from "@/lib/useRowDensity";
import { useCompactMoney } from "@/lib/useCompactMoney";

const GROUPS = [
  { v: "income", label: "Income" },
  { v: "cogs", label: "COGS" },
  { v: "expense", label: "Expense" },
  { v: "other", label: "Other" },
];

export default function Settings() {
  const { user, tenant, refreshMe } = useAuth();
  const visibleSections = useVisibleSettingsSections(user, tenant);
  // Persist last active tab per user so returning here lands on their last section.
  const tabStorageKey = `ll:settings:tab:${user?.email || "anon"}`;
  const [settingsTab, setSettingsTabRaw] = useState(() => {
    try {
      const saved = typeof window !== "undefined" ? window.localStorage.getItem(tabStorageKey) : null;
      return saved || "profile";
    } catch { return "profile"; }
  });
  const setSettingsTab = (id) => {
    setSettingsTabRaw(id);
    try { window.localStorage.setItem(tabStorageKey, id); } catch { /* ignore */ }
  };
  // If the saved tab is no longer visible (e.g. plan downgraded), fall back.
  useEffect(() => {
    if (!visibleSections.find((s) => s.id === settingsTab)) {
      setSettingsTab(visibleSections[0]?.id || "profile");
    }
    // eslint-disable-next-line
  }, [visibleSections.map((s) => s.id).join(",")]);
  const [customs, setCustoms] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [form, setForm] = useState({ name: "", group: "expense" });
  const [adding, setAdding] = useState(false);
  // Branding
  const [brand, setBrand] = useState({ brand_color: "", logo_base64: "" });
  const [savingBrand, setSavingBrand] = useState(false);
  const logoRef = useRef(null);

  const canManageBrand = user && (user.role === "owner" || user.role === "admin");
  const canEditCompany = canManageBrand;

  // Company edit modal state
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState(null);  // filled on open
  const [savingCompany, setSavingCompany] = useState(false);

  const openCompanyEdit = () => {
    setCompanyForm({
      name: tenant?.name || "",
      legal_name: tenant?.legal_name || "",
      website: tenant?.website || "",
      phone: tenant?.phone || "",
      tax_id: tenant?.tax_id || "",
      address_line1: tenant?.address_line1 || "",
      address_line2: tenant?.address_line2 || "",
      city: tenant?.city || "",
      state: tenant?.state || "",
      postal_code: tenant?.postal_code || "",
      country: tenant?.country || "",
    });
    setEditingCompany(true);
  };
  const closeCompanyEdit = () => { if (!savingCompany) { setEditingCompany(false); setCompanyForm(null); } };
  const saveCompany = async () => {
    if (!companyForm) return;
    const cleanName = (companyForm.name || "").trim();
    if (!cleanName) { toast.error("Company name can't be blank"); return; }
    setSavingCompany(true);
    try {
      const res = await api.patch("/tenant", companyForm);
      // Reflect any server-side normalization (e.g. "acme.com" → "https://acme.com")
      setCompanyForm((f) => ({
        ...f,
        website: res.data.website || "",
        phone: res.data.phone || "",
        tax_id: res.data.tax_id || "",
      }));
      await refreshMe();
      toast.success("Company info updated");
      setEditingCompany(false);
      setCompanyForm(null);
    } catch (err) {
      // 422 from Pydantic returns detail as an array of {loc, msg}
      const detail = err?.response?.data?.detail;
      let msg = "Update failed";
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : "field";
        const humanMsg = (first.msg || "").replace(/^Value error, /, "");
        msg = `${field}: ${humanMsg}`;
      } else if (typeof detail === "string") {
        msg = detail;
      }
      toast.error(msg);
    } finally { setSavingCompany(false); }
  };

  // Notifications
  const [notif, setNotif] = useState({ weekly_reminder: false, next_reminder_at: null, cron_description: "" });
  const [savingNotif, setSavingNotif] = useState(false);

  const loadNotif = async () => {
    try {
      const r = await api.get("/tenant/notifications");
      setNotif({
        weekly_reminder: !!r.data.weekly_reminder,
        next_reminder_at: r.data.next_reminder_at || null,
        cron_description: r.data.cron_description || "",
      });
    } catch {}
  };

  const saveNotif = async (next) => {
    setSavingNotif(true);
    try {
      const r = await api.put("/tenant/notifications", next);
      setNotif({
        weekly_reminder: !!r.data.weekly_reminder,
        next_reminder_at: r.data.next_reminder_at || null,
        cron_description: r.data.cron_description || "",
      });
      toast.success("Notification preferences updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSavingNotif(false); }
  };

  // Client portal preferences (reminder cadence + welcome copy + support email)
  const [portal, setPortal] = useState({
    client_reminder_enabled: true,
    client_reminder_days: 35,
    portal_welcome_message: "",
    portal_support_email: "",
  });
  const [savingPortal, setSavingPortal] = useState(false);
  useEffect(() => {
    if (!tenant) return;
    setPortal({
      client_reminder_enabled: tenant.client_reminder_enabled !== false,
      client_reminder_days: tenant.client_reminder_days || 35,
      portal_welcome_message: tenant.portal_welcome_message || "",
      portal_support_email: tenant.portal_support_email || "",
    });
  }, [tenant]);

  const savePortal = async (patch) => {
    setSavingPortal(true);
    try {
      const payload = { ...portal, ...patch };
      const res = await api.patch("/tenant", payload);
      setPortal({
        client_reminder_enabled: res.data.client_reminder_enabled !== false,
        client_reminder_days: res.data.client_reminder_days || 35,
        portal_welcome_message: res.data.portal_welcome_message || "",
        portal_support_email: res.data.portal_support_email || "",
      });
      await refreshMe();
      toast.success("Client portal settings updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg?.replace(/^Value error, /, "") : (typeof detail === "string" ? detail : "Save failed");
      toast.error(msg || "Save failed");
    } finally { setSavingPortal(false); }
  };

  const loadCats = async () => {
    setLoadingCats(true);
    try {
      const r = await api.get("/categories/custom");
      setCustoms(r.data);
    } finally { setLoadingCats(false); }
  };
  const loadBrand = async () => {
    try {
      const r = await api.get("/tenant/branding");
      setBrand({ brand_color: r.data.brand_color || "", logo_base64: r.data.logo_base64 || "" });
    } catch {}
  };
  useEffect(() => { loadCats(); loadBrand(); loadNotif(); }, []);

  const onLogoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_500_000) { toast.error("Logo must be under 1.5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setBrand((b) => ({ ...b, logo_base64: reader.result }));
    reader.readAsDataURL(f);
  };

  const saveBranding = async () => {
    setSavingBrand(true);
    try {
      await api.put("/tenant/branding", {
        brand_color: brand.brand_color || null,
        logo_base64: brand.logo_base64 || null,
        clear_logo: !brand.logo_base64,
      });
      toast.success("Branding updated");
      loadBrand();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save branding");
    } finally { setSavingBrand(false); }
  };

  const clearLogo = () => {
    setBrand((b) => ({ ...b, logo_base64: "" }));
    if (logoRef.current) logoRef.current.value = "";
  };

  const addCat = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) { toast.error("Name required"); return; }
    setAdding(true);
    try {
      await api.post("/categories/custom", { name, group: form.group });
      toast.success("Category added");
      setForm({ name: "", group: "expense" });
      loadCats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add category");
    } finally { setAdding(false); }
  };

  const delCat = async (id) => {
    if (!window.confirm("Delete this custom category? Existing transactions keep the label.")) return;
    try {
      await api.delete(`/categories/custom/${id}`);
      toast.success("Removed");
      loadCats();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const canManageCats = user && (user.role === "owner" || user.role === "admin" || user.role === "accountant");
  const grouped = GROUPS.map((g) => ({
    ...g,
    items: customs.filter((c) => c.group === g.v),
  }));

  return (
    <>
      <PageHeader title="Settings" subtitle="Profile & workspace" testid="settings-header" />
      <PageBody>
        <div className="flex flex-col lg:flex-row gap-6">
          <SettingsTabs sections={visibleSections} activeId={settingsTab} onChange={setSettingsTab} />
          <div className="flex-1 min-w-0">

        {/* ================= PROFILE ================= */}
        {settingsTab === "profile" && (
        <div data-testid="settings-section-profile">
          <div className="ll-card-flat p-6 mb-6" style={{ background: "#FAF5EE", borderColor: "#D6BFA0" }} data-testid="profile-card">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Profile</div>
            <div className="font-display font-bold text-2xl mt-1 mb-4">Your account</div>
            <Row label="Name" value={user?.name} />
            <Row label="Email" value={user?.email} />
            <Row label="Role" value={user?.role} />
          </div>

          {/* Appearance — per-user prefs shared across the whole app */}
          <AppearanceCard />

          {/* Mail routing — mailto vs. system (per-user) */}
          <MailPreferencesCard />

          {/* Notifications (per-user push + weekly reminder toggle) */}
          <div className="ll-card-flat p-6 mb-6" data-testid="notifications-card">
            <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5">
                  <Bell size={11} /> Alerts
                </div>
                <div className="font-display font-bold text-2xl mt-1">Notifications</div>
                <p className="text-sm text-[var(--ll-ink-soft)] mt-1 max-w-prose">
                  Get pinged in your browser (and on installed app home screens) when statements finish parsing,
                  Teller syncs complete, or it&apos;s time to close the week.
                </p>
              </div>
              <PushNotificationsToggle />
            </div>

            <div className="border-t pt-4 mt-4 flex flex-wrap items-center gap-4" style={{ borderColor: "var(--ll-border)" }}>
              <div className="flex-1 min-w-[260px]">
                <div className="text-sm font-medium flex items-center gap-2">
                  <CalendarClock size={14} /> Weekly upload reminder
                </div>
                <div className="text-xs text-[var(--ll-ink-soft)] mt-1">
                  {notif.cron_description || "Every Friday at 5:00 PM (UTC)"}, send a push reminder to every team member with notifications enabled.
                </div>
                {notif.weekly_reminder && notif.next_reminder_at && (
                  <div
                    className="text-[10px] uppercase tracking-[0.2em] mt-2 font-mono-fin"
                    style={{ color: "#065F46" }}
                    data-testid="next-reminder-at"
                  >
                    Next reminder · {(() => {
                      const d = new Date(notif.next_reminder_at);
                      const dateStr = d.toLocaleDateString(undefined, {
                        weekday: "short", month: "short", day: "numeric",
                      });
                      const timeStr = d.toLocaleTimeString(undefined, {
                        hour: "numeric", minute: "2-digit", timeZoneName: "short",
                      });
                      return `${dateStr} · ${timeStr}`;
                    })()}
                  </div>
                )}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={notif.weekly_reminder}
                disabled={savingNotif}
                onClick={() => saveNotif({ weekly_reminder: !notif.weekly_reminder })}
                data-testid="weekly-reminder-toggle"
                className="relative w-12 h-6 rounded-full transition-colors disabled:opacity-60"
                style={{ background: notif.weekly_reminder ? "#10B981" : "#CBD5E1" }}
              >
                <span
                  className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
                  style={{ transform: notif.weekly_reminder ? "translateX(24px)" : "translateX(0)" }}
                />
              </button>
            </div>
          </div>
        </div>
        )}

        {/* ================= WORKSPACE ================= */}
        {settingsTab === "workspace" && (
        <div data-testid="settings-section-workspace">
          <div
            className="ll-card-flat p-6 mb-6 [&_*]:!text-black relative"
            style={{ background: "#FFFFFF", borderColor: "#BFDBFE" }}
            data-testid="workspace-card"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em]">Workspace</div>
                <div className="font-display font-bold text-2xl mt-1 break-words" data-testid="workspace-name">{tenant?.name}</div>
                {tenant?.legal_name && tenant.legal_name !== tenant.name && (
                  <div className="text-xs text-[var(--ll-ink-soft)] italic mt-0.5">Legal name: {tenant.legal_name}</div>
                )}
              </div>
              {canEditCompany && (
                <button
                  onClick={openCompanyEdit}
                  data-testid="workspace-edit-btn"
                  title="Edit company name & information"
                  className="text-xs px-2.5 py-1.5 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5 shrink-0"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  <Pencil size={12} /> Edit
                </button>
              )}
            </div>
            <Row label="Plan" value={tenant?.plan} />
            <Row label="Subscription" value={tenant?.subscription_status} />
            <Row label="Bank account limit" value={tenant?.bank_account_limit} />
            <Row label="Team members" value={tenant?.seats_limit} />
            <Row label="Owner email" value={tenant?.owner_email} />
            {(tenant?.website || tenant?.phone || tenant?.tax_id) && (
              <div className="border-t mt-3 pt-3 space-y-0" style={{ borderColor: "#E4E9E3" }}>
                {tenant?.website && <Row label="Website" value={tenant.website} />}
                {tenant?.phone && <Row label="Phone" value={tenant.phone} />}
                {tenant?.tax_id && <Row label="Tax ID / EIN" value={tenant.tax_id} />}
              </div>
            )}
            {(tenant?.address_line1 || tenant?.city) && (
              <div className="border-t mt-3 pt-3" style={{ borderColor: "#E4E9E3" }}>
                <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Address</div>
                <div className="text-xs whitespace-pre-line" data-testid="workspace-address">
                  {[
                    tenant.address_line1,
                    tenant.address_line2,
                    [tenant.city, tenant.state, tenant.postal_code].filter(Boolean).join(", "),
                    tenant.country,
                  ].filter(Boolean).join("\n")}
                </div>
              </div>
            )}
          </div>

        {/* Reclaim unused add-on slots (empty paid seats / bank slots) */}
        <ReclaimSlotsCard />

        {/* Workspace theme picker — row hover + accent across the app */}
        <ThemePickerCard />

        {/* Branded signup/sign-in link + slug */}
        <WorkspaceSlugCard />

        {/* Team-member add-on slots (Pro only) */}
        <TeamMemberSlotsCard />

        {/* Bank-account add-on slots (Pro only) */}
        <BankAccountSlotsCard />

        {/* Branding */}
        <div className="ll-card-flat p-6 mb-6" data-testid="branding-card">
          <div className="flex items-start justify-between mb-4 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5"><Palette size={11} /> Identity</div>
              <div className="font-display font-bold text-2xl mt-1">Branding</div>
              <p className="text-sm text-[var(--ll-ink-soft)] mt-1">
                Customize the logo and accent color used on exported PDFs (close package, reports) and across the workspace.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Logo */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-2">Workspace logo</label>
              <div className="flex items-center gap-4">
                <div
                  className="w-24 h-24 border bg-white grid place-items-center overflow-hidden"
                  style={{ borderColor: "var(--ll-border)" }}
                  data-testid="branding-logo-preview"
                >
                  {brand.logo_base64 ? (
                    <img src={brand.logo_base64} alt="logo" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <ImageIcon size={24} className="text-[var(--ll-ink-muted)]" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <label className="inline-flex items-center gap-1.5 text-sm px-3 py-2 border cursor-pointer hover:bg-[var(--ll-mint)]" style={{ borderColor: "var(--ll-border)" }} data-testid="branding-upload-label">
                    <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={onLogoFile} className="hidden" data-testid="branding-logo-input" disabled={!canManageBrand} />
                    <Upload size={13} /> Upload logo
                  </label>
                  {brand.logo_base64 && (
                    <button onClick={clearLogo} className="ml-2 text-xs text-[var(--ll-error)] hover:underline inline-flex items-center gap-1" data-testid="branding-logo-clear">
                      <X size={12} /> Remove
                    </button>
                  )}
                  <div className="text-[11px] text-[var(--ll-ink-soft)]">PNG / JPG / SVG, max 1.5MB.</div>
                </div>
              </div>
            </div>

            {/* Brand color */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-2">Brand accent color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={brand.brand_color || "#1B4332"}
                  onChange={(e) => setBrand({ ...brand, brand_color: e.target.value })}
                  data-testid="branding-color-input"
                  disabled={!canManageBrand}
                  className="w-14 h-14 border bg-white p-1 cursor-pointer"
                  style={{ borderColor: "var(--ll-border)" }}
                />
                <input
                  type="text"
                  value={brand.brand_color || ""}
                  onChange={(e) => setBrand({ ...brand, brand_color: e.target.value })}
                  placeholder="#1B4332"
                  data-testid="branding-color-text"
                  disabled={!canManageBrand}
                  className="font-mono-fin px-3 py-2 border bg-white text-sm uppercase"
                  style={{ borderColor: "var(--ll-border)" }}
                />
                <button
                  onClick={() => setBrand({ ...brand, brand_color: "" })}
                  className="text-xs underline text-[var(--ll-ink-soft)]"
                  data-testid="branding-color-clear"
                >
                  Reset
                </button>
              </div>
              <div className="mt-3 p-3 border" style={{ background: brand.brand_color || "#1B4332", borderColor: "var(--ll-border)" }} data-testid="branding-color-preview">
                <div className="text-xs uppercase tracking-widest text-white/80">Preview</div>
                <div className="font-display font-bold text-lg text-white">Period Close Package</div>
              </div>
            </div>
          </div>

          {canManageBrand && (
            <div className="mt-5 flex justify-end">
              <button
                onClick={saveBranding}
                disabled={savingBrand}
                data-testid="branding-save-btn"
                className="px-4 py-2 bg-[var(--ll-ink)] text-white text-sm hover:bg-[#2D6A4F] disabled:opacity-60"
              >
                {savingBrand ? "Saving…" : "Save branding"}
              </button>
            </div>
          )}
        </div>

        {/* Custom categories (part of Workspace taxonomy) */}
        <div className="ll-card-flat p-6 mb-6" data-testid="custom-categories-card">
          <div className="flex items-start justify-between mb-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5"><Tag size={11} /> Taxonomy</div>
              <div className="font-display font-bold text-2xl mt-1">Custom categories</div>
              <p className="text-sm text-[var(--ll-ink-soft)] mt-1">
                Add categories specific to your business. They&apos;ll appear alongside the built-in taxonomy in transactions and reports.
              </p>
            </div>
          </div>

          {canManageCats && (
            <form onSubmit={addCat} className="grid sm:grid-cols-[1fr,180px,auto] gap-3 mt-5 mb-6" data-testid="add-category-form">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Cloud Infrastructure"
                data-testid="custom-cat-name"
                className="px-3 py-2 border bg-white text-sm"
                style={{ borderColor: "var(--ll-border)" }}
              />
              <select
                value={form.group}
                onChange={(e) => setForm({ ...form, group: e.target.value })}
                data-testid="custom-cat-group"
                className="px-3 py-2 border bg-white text-sm"
                style={{ borderColor: "var(--ll-border)" }}
              >
                {GROUPS.map((g) => <option key={g.v} value={g.v}>{g.label}</option>)}
              </select>
              <button
                type="submit"
                disabled={adding}
                data-testid="custom-cat-submit"
                className="px-4 py-2 bg-[var(--ll-ink)] text-white text-sm hover:bg-[#2D6A4F] flex items-center gap-1.5 disabled:opacity-60"
              >
                <Plus size={14} /> {adding ? "Adding…" : "Add"}
              </button>
            </form>
          )}

          {loadingCats && <div className="ll-progress mb-4" />}

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: "var(--ll-border)" }} data-testid="custom-categories-grid">
            {grouped.map((g) => (
              <div key={g.v} className="bg-[var(--ll-bg-card)] p-4" data-testid={`cat-group-${g.v}`}>
                <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2">{g.label}</div>
                {g.items.length === 0 ? (
                  <div className="text-xs text-[var(--ll-ink-muted)] italic">None yet</div>
                ) : (
                  <ul className="space-y-1.5">
                    {g.items.map((c) => (
                      <li key={c.id} className="flex items-center justify-between text-sm group" data-testid={`cat-item-${c.id}`}>
                        <span className="truncate">{c.name}</span>
                        {canManageCats && (
                          <button
                            onClick={() => delCat(c.id)}
                            data-testid={`cat-delete-${c.id}`}
                            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-[var(--ll-error)] hover:text-white transition-opacity"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
        </div>
        )}

        {/* ================= CLIENT PORTAL ================= */}
        {settingsTab === "client-portal" && canManageBrand && (
        <div data-testid="settings-section-client-portal">
        <div className="ll-card-flat p-6 mb-6" data-testid="client-portal-card">
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5">
              <Bell size={11} /> Client portal
            </div>
            <div className="font-display font-bold text-2xl mt-1">Client portal</div>
            <p className="text-sm text-[var(--ll-ink-soft)] mt-1 max-w-prose">
              Customize the upload portal your clients see, and choose how often we nudge them to send in their statements.
            </p>
          </div>

          <div className="border-t pt-4 mt-2 flex items-start justify-between gap-4 flex-wrap" style={{ borderColor: "var(--ll-border)" }}>
            <div className="flex-1 min-w-[260px]">
              <div className="text-sm font-medium flex items-center gap-2">
                <CalendarClock size={14} /> Auto-email reminder cadence
              </div>
              <div className="text-xs text-[var(--ll-ink-soft)] mt-1">
                Clients who haven&apos;t uploaded in the selected number of days get a friendly nudge by email. 7-day cooldown per client.
              </div>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="reminder-cadence-radios">
                {[15, 30, 45, 60].map((d) => {
                  const active = portal.client_reminder_days === d;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => savePortal({ client_reminder_days: d })}
                      disabled={savingPortal || !portal.client_reminder_enabled}
                      data-testid={`reminder-cadence-${d}`}
                      className="px-3 py-1.5 text-xs border transition-colors disabled:opacity-50"
                      style={{
                        borderColor: active ? "var(--ll-ink)" : "var(--ll-border)",
                        background: active ? "var(--ll-ink)" : "white",
                        color: active ? "white" : "var(--ll-ink)",
                      }}
                    >
                      Every {d} days
                    </button>
                  );
                })}
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={portal.client_reminder_enabled}
              disabled={savingPortal}
              onClick={() => savePortal({ client_reminder_enabled: !portal.client_reminder_enabled })}
              data-testid="client-reminder-toggle"
              className="relative w-12 h-6 rounded-full transition-colors disabled:opacity-60"
              style={{ background: portal.client_reminder_enabled ? "#10B981" : "#CBD5E1" }}
            >
              <span
                className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
                style={{ transform: portal.client_reminder_enabled ? "translateX(24px)" : "translateX(0)" }}
              />
            </button>
          </div>

          <div className="border-t pt-4 mt-4" style={{ borderColor: "var(--ll-border)" }}>
            <label className="block text-sm font-medium mb-1">Welcome message</label>
            <div className="text-xs text-[var(--ll-ink-soft)] mb-2">Shown at the top of the client portal. Up to 600 characters.</div>
            <textarea
              rows={3}
              maxLength={600}
              value={portal.portal_welcome_message}
              onChange={(e) => setPortal({ ...portal, portal_welcome_message: e.target.value })}
              placeholder="e.g. Hi! Drop your monthly bank statement here and we'll handle the rest — usually within 24 hours."
              data-testid="portal-welcome-input"
              className="w-full px-3 py-2 border text-sm"
              style={{ borderColor: "var(--ll-border)" }}
            />
            <div className="flex items-center justify-between mt-2">
              <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{portal.portal_welcome_message.length}/600</div>
              <button
                type="button"
                onClick={() => savePortal({ portal_welcome_message: portal.portal_welcome_message })}
                disabled={savingPortal}
                data-testid="portal-welcome-save"
                className="text-xs px-3 py-1.5 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] flex items-center gap-1.5 disabled:opacity-50"
              >
                <Save size={12} /> Save message
              </button>
            </div>
          </div>

          <div className="border-t pt-4 mt-4" style={{ borderColor: "var(--ll-border)" }}>
            <label className="block text-sm font-medium mb-1">Support email</label>
            <div className="text-xs text-[var(--ll-ink-soft)] mb-2">Displayed in the portal footer so clients can reach a real human.</div>
            <div className="flex items-center gap-2">
              <input
                type="email"
                value={portal.portal_support_email}
                onChange={(e) => setPortal({ ...portal, portal_support_email: e.target.value })}
                placeholder="support@yourfirm.com"
                data-testid="portal-support-email-input"
                className="flex-1 px-3 py-2 border text-sm"
                style={{ borderColor: "var(--ll-border)" }}
              />
              <button
                type="button"
                onClick={() => savePortal({ portal_support_email: portal.portal_support_email })}
                disabled={savingPortal}
                data-testid="portal-support-email-save"
                className="text-xs px-3 py-1.5 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] flex items-center gap-1.5 disabled:opacity-50"
              >
                <Save size={12} /> Save
              </button>
            </div>
          </div>
        </div>
        </div>
        )}

        {/* ================= AUTOMATIONS ================= */}
        {settingsTab === "automations" && (
        <div data-testid="settings-section-automations">
          {/* Transaction categorization rules */}
          <TransactionRulesCard />

          {/* Auto report publishing */}
          {canManageBrand && <AutoReportsCard />}

          {/* Flag follow-up templates */}
          <FlagTemplatesCard />

          {/* A/R Chase Playbook — three tone tiers */}
          {["owner", "admin"].includes(user?.role) && <ARChaseTemplatesCard />}
        </div>
        )}

        {/* ================= COMPLIANCE ================= */}
        {settingsTab === "compliance" && ["owner", "admin"].includes(user?.role) && (
        <div data-testid="settings-section-compliance">
          <LockedPeriodsCard />
        </div>
        )}

        {/* ================= INTEGRATIONS ================= */}
        {settingsTab === "integrations" && (
        <div data-testid="settings-section-integrations">
          <div className="ll-card-flat p-6 mb-6" data-testid="integrations-card">
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5">
                <Plug size={11} /> Integrations
              </div>
              <div className="font-display font-bold text-2xl mt-1">Connect your other tools</div>
              <p className="text-sm text-[var(--ll-ink-soft)] mt-1 max-w-prose">
                Push cleaned data from Financials Ready into the systems your team already uses.
                QuickBooks Online is coming soon — click Connect to be first in line.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4" data-testid="integrations-grid">
              {/* QuickBooks Online — live card wired to backend */}
              <QBOCard />

              {/* Stripe (already live via Billing) */}
              <div className="border p-5 flex flex-col gap-3" style={{ borderColor: "var(--ll-border)" }} data-testid="integration-stripe">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">Stripe</div>
                    <div className="text-[11px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-0.5">Payments · Already enabled</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 text-white" style={{ background: "#10B981" }}>Live</span>
                </div>
                <p className="text-xs text-[var(--ll-ink-soft)] leading-relaxed">
                  Collect subscription payments and invoice payments. Manage plans and billing portal from the Billing page.
                </p>
                <a
                  href="/app/billing"
                  data-testid="integration-stripe-manage"
                  className="self-start text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)]"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  Manage billing →
                </a>
              </div>

              {/* Teller — only visible if the tenant has it configured */}
              <div className="border p-5 flex flex-col gap-3" style={{ borderColor: "var(--ll-border)" }} data-testid="integration-teller">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">Teller</div>
                    <div className="text-[11px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-0.5">Banking · Auto-sync</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[var(--ll-bg-card)] border" style={{ borderColor: "var(--ll-border)" }}>Optional</span>
                </div>
                <p className="text-xs text-[var(--ll-ink-soft)] leading-relaxed">
                  Link your bank directly for automatic statement sync. Configure Teller credentials from the Bank Accounts page.
                </p>
                <a
                  href="/app/accounts"
                  data-testid="integration-teller-manage"
                  className="self-start text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)]"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  Manage bank links →
                </a>
              </div>
            </div>
          </div>
        </div>
        )}

          </div>
        </div>

        {/* Company info edit modal */}
        {editingCompany && companyForm && (
          <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="company-edit-modal">
            <div className="bg-white max-w-2xl w-full max-h-[92vh] overflow-auto border shadow-xl" style={{ borderColor: "var(--ll-border)" }}>
              <div className="px-6 py-4 border-b flex items-center justify-between gap-3 sticky top-0 bg-white z-[1]" style={{ borderColor: "var(--ll-border)" }}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 grid place-items-center text-white shrink-0" style={{ background: "var(--ll-ink)" }}>
                    <Building2 size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Company profile</div>
                    <div className="font-display font-bold text-xl truncate">Edit company info</div>
                  </div>
                </div>
                <button
                  onClick={closeCompanyEdit}
                  data-testid="company-edit-close"
                  className="p-1.5 text-[var(--ll-ink-muted)] hover:text-[var(--ll-ink)] hover:bg-[var(--ll-bg-sidebar)]"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="px-6 py-5 space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2">Identity</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Display name *" required>
                      <input
                        type="text" value={companyForm.name}
                        onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                        data-testid="company-field-name"
                        placeholder="Acme Studios"
                        maxLength={120}
                        className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                      />
                    </Field>
                    <Field label="Legal / registered name">
                      <input
                        type="text" value={companyForm.legal_name}
                        onChange={(e) => setCompanyForm({ ...companyForm, legal_name: e.target.value })}
                        data-testid="company-field-legal-name"
                        placeholder="Acme Studios, Inc."
                        maxLength={200}
                        className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                      />
                    </Field>
                    <Field label="Website">
                      <input
                        type="text" value={companyForm.website}
                        onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                        data-testid="company-field-website"
                        placeholder="acme.com or https://acme.com"
                        maxLength={200}
                        className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        type="text" value={companyForm.phone}
                        onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                        data-testid="company-field-phone"
                        placeholder="+1 (555) 123-4567"
                        maxLength={40}
                        className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                      />
                    </Field>
                    <Field label="Tax ID / EIN">
                      <input
                        type="text" value={companyForm.tax_id}
                        onChange={(e) => setCompanyForm({ ...companyForm, tax_id: e.target.value })}
                        data-testid="company-field-tax-id"
                        placeholder="12-3456789 or GB123456789"
                        maxLength={40}
                        className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                      />
                    </Field>
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2">Registered address</div>
                  <div className="space-y-3">
                    <Field label="Address line 1">
                      <input
                        type="text" value={companyForm.address_line1}
                        onChange={(e) => setCompanyForm({ ...companyForm, address_line1: e.target.value })}
                        data-testid="company-field-line1"
                        placeholder="123 Main Street"
                        maxLength={200}
                        className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                      />
                    </Field>
                    <Field label="Address line 2">
                      <input
                        type="text" value={companyForm.address_line2}
                        onChange={(e) => setCompanyForm({ ...companyForm, address_line2: e.target.value })}
                        data-testid="company-field-line2"
                        placeholder="Suite 400"
                        maxLength={200}
                        className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                      />
                    </Field>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <Field label="City">
                        <input
                          type="text" value={companyForm.city}
                          onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                          data-testid="company-field-city"
                          maxLength={100}
                          className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                        />
                      </Field>
                      <Field label="State / Region">
                        <input
                          type="text" value={companyForm.state}
                          onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value })}
                          data-testid="company-field-state"
                          maxLength={100}
                          className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                        />
                      </Field>
                      <Field label="Postal code">
                        <input
                          type="text" value={companyForm.postal_code}
                          onChange={(e) => setCompanyForm({ ...companyForm, postal_code: e.target.value })}
                          data-testid="company-field-postal"
                          maxLength={20}
                          className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                        />
                      </Field>
                      <Field label="Country">
                        <input
                          type="text" value={companyForm.country}
                          onChange={(e) => setCompanyForm({ ...companyForm, country: e.target.value })}
                          data-testid="company-field-country"
                          maxLength={100}
                          className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                        />
                      </Field>
                    </div>
                  </div>
                </div>

                <div className="text-[11px] text-[var(--ll-ink-soft)] bg-[var(--ll-bg-card)] border px-3 py-2" style={{ borderColor: "var(--ll-border)" }}>
                  These details are surfaced on your exported reports and invoices — accountants and tax authorities look here first. Plan, seat, and billing fields are managed on the <strong>Billing</strong> page and can&apos;t be changed here.
                </div>
              </div>

              <div className="px-6 py-4 border-t flex items-center justify-end gap-2 bg-[var(--ll-bg-card)] sticky bottom-0" style={{ borderColor: "var(--ll-border)" }}>
                <button
                  onClick={closeCompanyEdit}
                  disabled={savingCompany}
                  data-testid="company-edit-cancel"
                  className="text-sm px-4 py-2 border hover:bg-white disabled:opacity-60"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveCompany}
                  disabled={savingCompany || !companyForm.name?.trim()}
                  data-testid="company-edit-save"
                  className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5 disabled:opacity-60"
                >
                  <Save size={13} /> {savingCompany ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}

const Row = ({ label, value }) => (
  <div className="grid grid-cols-2 gap-3 py-2 border-b" style={{ borderColor: "var(--ll-border)" }}>
    <div className="text-xs uppercase tracking-widest text-[var(--ll-ink-soft)]">{label}</div>
    <div className="text-sm">{value || "—"}</div>
  </div>
);

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">
      {label} {required && <span className="text-[var(--ll-error)]" aria-hidden>*</span>}
    </span>
    {children}
  </label>
);


// Appearance card — exposes the shared row-density + compact-money prefs
// so a user can set them without visiting a data-heavy page first. All
// changes propagate instantly across the app via the shared hooks.
function AppearanceCard() {
  const { density, setDensity } = useRowDensity();
  const { compact, toggle: toggleCompact } = useCompactMoney();
  const DENSITY_OPTIONS = [
    { key: "compact", icon: Rows4, label: "Compact", desc: "Pack more rows per screen for power-users scanning long lists." },
    { key: "cozy", icon: Rows3, label: "Cozy", desc: "Default balanced spacing." },
    { key: "comfortable", icon: Rows2, label: "Comfortable", desc: "Generous padding, easier to tap on touch." },
  ];
  return (
    <div className="ll-card-flat p-6 mb-6" data-testid="appearance-card">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5">
            <SlidersHorizontal size={11} /> Personal preferences
          </div>
          <div className="font-display font-bold text-2xl mt-1">Appearance</div>
          <div className="text-xs text-[var(--ll-ink-soft)] mt-1 max-w-prose">
            Saved per browser. Applies to Transactions, Reconciliation, Statements, and the Review queue instantly.
          </div>
        </div>
      </div>

      {/* Row density */}
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2">Row density</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Row density">
          {DENSITY_OPTIONS.map(({ key, icon: Icon, label, desc }) => {
            const active = density === key;
            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setDensity(key)}
                data-testid={`settings-density-${key}`}
                className={`text-left p-3 border transition-colors ${active ? "bg-[#0b5d9e] text-white border-[#0b5d9e]" : "hover:bg-[var(--ll-mint)]"}`}
                style={{ borderColor: active ? "#0b5d9e" : "var(--ll-border)" }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={14} />
                  <span className="font-medium text-sm">{label}</span>
                </div>
                <div className={`text-[11px] leading-snug ${active ? "text-white/85" : "text-[var(--ll-ink-soft)]"}`}>{desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Compact money */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2">Compact amounts</div>
        <button
          type="button"
          onClick={toggleCompact}
          aria-pressed={compact}
          data-testid="settings-compact-toggle"
          className={`w-full text-left p-3 border transition-colors flex items-start gap-3 ${compact ? "bg-[#0b5d9e] text-white border-[#0b5d9e]" : "hover:bg-[var(--ll-mint)]"}`}
          style={{ borderColor: compact ? "#0b5d9e" : "var(--ll-border)" }}
        >
          <div className={`w-9 h-9 shrink-0 border grid place-items-center ${compact ? "border-white/60" : "border-[var(--ll-border)]"}`}>
            {compact ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm">
              {compact ? "Compact amounts on" : "Full amounts on"}
            </div>
            <div className={`text-[11px] leading-snug mt-0.5 ${compact ? "text-white/85" : "text-[var(--ll-ink-soft)]"}`}>
              {compact
                ? "KPI cards and headline totals render as $246.3K / $1.95M — tap to switch back to full precision."
                : "KPI cards and headline totals render full precision ($246,281.92). Great for close-the-books accuracy; tap to switch to compact for phone-friendly glances."}
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}


// Auto-publish scheduled reports to every active client on the 1st of the
// month. Rendered inside the Settings page (owner/admin only — guarded at
// the callsite).
function AutoReportsCard() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ report_kind: "pnl", file_type: "pdf", note: "", enabled: true, cadence: "monthly" });
  const [creating, setCreating] = useState(false);
  const [runningId, setRunningId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/scheduled-reports");
      setItems(r.data.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/scheduled-reports", form);
      toast.success(form.cadence === "weekly"
        ? "Scheduled — will auto-publish every Monday"
        : "Scheduled — will auto-publish on the 1st of each month");
      setForm({ report_kind: "pnl", file_type: "pdf", note: "", enabled: true, cadence: "monthly" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setCreating(false); }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/scheduled-reports/${s.id}`, { enabled: !s.enabled });
      await load();
    } catch { toast.error("Toggle failed"); }
  };
  const runNow = async (s) => {
    setRunningId(s.id);
    try {
      const r = await api.post(`/scheduled-reports/${s.id}/run-now`);
      toast.success(`Published to ${r.data.count} client${r.data.count === 1 ? "" : "s"}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Run failed");
    } finally { setRunningId(null); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove the ${s.report_kind} auto-publish schedule?`)) return;
    try {
      await api.delete(`/scheduled-reports/${s.id}`);
      await load();
    } catch { toast.error("Delete failed"); }
  };

  const KIND_LABEL = {
    pnl: "Profit & Loss",
    balance_sheet: "Balance Sheet",
    expenses: "Expense Report",
    cash_flow: "Cash Flow",
    income_statement: "Income Statement",
  };

  return (
    <div className="ll-card-flat p-6 mb-6" data-testid="auto-reports-card">
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5">
          <Send size={11} /> Automation
        </div>
        <div className="font-display font-bold text-2xl mt-1">Auto report publishing</div>
        <p className="text-sm text-[var(--ll-ink-soft)] mt-1 max-w-prose">
          Schedule a report to auto-publish to every active client on the 1st of each month. The period always
          covers the previous calendar month, so your first-of-May run ships April&apos;s numbers.
        </p>
      </div>

      {items.length === 0 ? (
        <div className="border p-4 text-sm text-[var(--ll-ink-soft)] text-center" style={{ borderColor: "var(--ll-border)" }} data-testid="auto-reports-empty">
          No auto-publish schedules yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="auto-reports-list">
          {items.map((s) => (
            <div
              key={s.id}
              className="border p-3 flex items-center gap-3"
              style={{ borderColor: "var(--ll-border)" }}
              data-testid={`sched-row-${s.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">
                  {KIND_LABEL[s.report_kind] || s.report_kind}
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{s.file_type}</span>
                  {s.enabled ? (
                    <span className="ml-2 text-[10px] uppercase tracking-widest px-1.5 py-0.5" style={{ background: "#D1FAE5", color: "#065F46" }}>Active</span>
                  ) : (
                    <span className="ml-2 text-[10px] uppercase tracking-widest px-1.5 py-0.5" style={{ background: "#F1F5F9", color: "#64748B" }}>Paused</span>
                  )}
                </div>
                <div className="text-xs text-[var(--ll-ink-soft)] mt-0.5">
                  {(s.cadence === "weekly") ? "Runs every Monday" : "Runs on the 1st of every month"}
                  {s.last_run_at ? ` · last run ${s.last_run_at.slice(0, 10)} (${s.last_run_count} client${s.last_run_count === 1 ? "" : "s"})` : " · never run yet"}
                </div>
                {s.note && <div className="text-xs mt-1 italic text-[var(--ll-ink-soft)]">&ldquo;{s.note}&rdquo;</div>}
              </div>
              <button
                onClick={() => runNow(s)}
                disabled={runningId === s.id}
                data-testid={`sched-run-${s.id}`}
                className="text-xs px-2 py-1.5 border hover:bg-[var(--ll-mint)] flex items-center gap-1 disabled:opacity-50"
                style={{ borderColor: "var(--ll-border)" }}
                title="Run now"
              >
                {runningId === s.id ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />}
                Run now
              </button>
              <button
                onClick={() => toggle(s)}
                data-testid={`sched-toggle-${s.id}`}
                className="text-xs px-2 py-1.5 border hover:bg-[var(--ll-mint)]"
                style={{ borderColor: "var(--ll-border)" }}
              >
                {s.enabled ? "Pause" : "Enable"}
              </button>
              <button
                onClick={() => remove(s)}
                data-testid={`sched-delete-${s.id}`}
                aria-label="Remove schedule"
                className="p-1.5 text-[var(--ll-ink-muted)] hover:text-[#DC2626]"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={submit} className="border-t pt-4 mt-4 grid gap-3" style={{ borderColor: "var(--ll-border)" }} data-testid="sched-new-form">
        <div className="text-xs uppercase tracking-widest text-[var(--ll-ink-muted)]">Add a schedule</div>
        <div className="grid grid-cols-3 gap-3">
          <select
            value={form.report_kind}
            onChange={(e) => setForm({ ...form, report_kind: e.target.value })}
            data-testid="sched-new-kind"
            className="px-3 py-2 border bg-white text-sm"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <option value="pnl">Profit & Loss</option>
            <option value="balance_sheet">Balance Sheet</option>
            <option value="expenses">Expense Report</option>
            <option value="cash_flow">Cash Flow</option>
            <option value="income_statement">Income Statement</option>
          </select>
          <select
            value={form.cadence}
            onChange={(e) => setForm({ ...form, cadence: e.target.value })}
            data-testid="sched-new-cadence"
            className="px-3 py-2 border bg-white text-sm"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <option value="monthly">Monthly (1st)</option>
            <option value="weekly">Weekly (Mon)</option>
          </select>
          <select
            value={form.file_type}
            onChange={(e) => setForm({ ...form, file_type: e.target.value })}
            data-testid="sched-new-format"
            className="px-3 py-2 border bg-white text-sm"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <option value="pdf">PDF</option>
            <option value="xlsx">Excel</option>
          </select>
        </div>
        <input
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder="Note attached to every publication (optional)"
          maxLength={500}
          data-testid="sched-new-note"
          className="px-3 py-2 border text-sm"
          style={{ borderColor: "var(--ll-border)" }}
        />
        <button
          type="submit"
          disabled={creating}
          data-testid="sched-new-submit"
          className="text-sm px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] flex items-center gap-1.5 w-fit disabled:opacity-50"
        >
          {creating ? <><Loader2 size={12} className="animate-spin" /> Adding…</> : <><Plus size={12} /> Add schedule</>}
        </button>
      </form>
    </div>
  );
}
